import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Award, Shield, Star, Lock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockData } from '../utils/mockData';

const WhyUs = () => {
  const iconMap = {
    'Zap': Zap,
    'Award': Award,
    'Shield': Shield,
    'Star': Star,
    'Lock': Lock
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-[#0a1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 break-words px-4">Why Work With Us</h1>
            <p className="text-lg sm:text-xl text-gray-300 px-4">
              Our unique approach delivers results that matter for your business
            </p>
          </div>
        </div>
      </section>

      {/* Main Differentiators */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
                A Different Kind of Recruiting Partner
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We're not a traditional staffing agency. We're a boutique recruiting firm built on military values, industry expertise, and a commitment to quality over quantity.
              </p>
            </div>

            <div className="space-y-8">
              {mockData.differentiators.map((item, idx) => {
                const IconComponent = iconMap[item.icon] || CheckCircle;
                
                return (
                  <div key={item.title} className="flex flex-col md:flex-row gap-6 items-start bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-[#d4a574] rounded-full shadow-lg">
                        <IconComponent className="text-[#0a1628]" size={40} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-[#0a1628] mb-3">{item.title}</h3>
                      <p className="text-lg text-gray-700 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
                Quality Over Quantity, Every Time
              </h2>
              <p className="text-xl text-gray-600">
                We don't believe in "spray and pray" recruiting. Here's what makes us different:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-[#d4a574]">
                <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Traditional Staffing Agencies</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 font-bold mt-1">×</span>
                    <span>High-volume, low-quality candidates</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 font-bold mt-1">×</span>
                    <span>Contract and temp positions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 font-bold mt-1">×</span>
                    <span>Little to no candidate vetting</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 font-bold mt-1">×</span>
                    <span>Generic recruiting approach</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 font-bold mt-1">×</span>
                    <span>High agency overhead costs</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#0a1628] p-8 rounded-lg shadow-lg border-l-4 border-[#d4a574]">
                <h3 className="text-2xl font-bold text-white mb-4">Service2Success Approach</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span>Qualified shortlists for quick decisions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span>Direct hire, salary-based placements only</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span>Thorough human evaluation process</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span>Targeted outreach to right candidates</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span>Lean model with better value</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Veteran Advantage */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
                The Veteran Advantage
              </h2>
              <p className="text-xl text-gray-600">
                Why veteran talent is a strategic asset for your business
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#d4a574] mb-4">Mission-Driven</div>
                <p className="text-gray-700">
                  Veterans are trained to complete the mission, no matter what it takes
                </p>
              </div>
              
              <div className="text-center">
                <div className="text-5xl font-bold text-[#d4a574] mb-4">Disciplined</div>
                <p className="text-gray-700">
                  Military training instills work ethic, reliability, and attention to detail
                </p>
              </div>
              
              <div className="text-center">
                <div className="text-5xl font-bold text-[#d4a574] mb-4">High-Performing</div>
                <p className="text-gray-700">
                  Proven ability to perform under pressure and exceed expectations
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial/Quote Section */}
      <section className="py-20 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <Star className="inline text-[#d4a574] mb-4" size={48} />
            </div>
            <blockquote className="text-2xl md:text-3xl font-semibold italic mb-6">
              "Helping companies hire the right people the first time while supporting those who served our country."
            </blockquote>
            <p className="text-xl text-gray-300">
              Rich Gallagher, Founder & CEO
            </p>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
                Our Simple, Effective Process
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full text-[#0a1628] text-2xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-3">Discovery Call</h3>
                <p className="text-gray-600">
                  30-minute needs assessment to understand your requirements
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full text-[#0a1628] text-2xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-3">Targeted Outreach</h3>
                <p className="text-gray-600">
                  Human evaluation and qualification of top candidates
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full text-[#0a1628] text-2xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-3">Success Partnership</h3>
                <p className="text-gray-600">
                  Ongoing support through offer, onboarding, and beyond
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Let's discuss how our veteran-forward approach can help you build a high-performing team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=hire">
                <Button size="lg" className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                  Hire Talent <ArrowRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white">
                  Schedule a Free Discovery Call
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Call rich@s2stalent.com | 513-668-0344
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
